import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { X } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../button";

export interface ModalAction {
  label: React.ReactNode;
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  paperClassName?: string;
  contentClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  showCloseButton?: boolean;
  action?: ModalAction;
  maxWidth?: false | "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  fullScreen?: boolean;
}

const paperSx = {
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 24px 80px rgba(15, 23, 42, 0.2)",
};

const CustomModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  className,
  paperClassName,
  contentClassName,
  titleClassName,
  descriptionClassName,
  showCloseButton = true,
  action,
  maxWidth = "sm",
  fullWidth = true,
  fullScreen = false,
}) => {
  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          onClose();
        }
      }}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      className={className}
      slotProps={{
        backdrop: {
          className: "bg-black/20",
        },
        paper: {
          className: cn("relative", paperClassName),
          sx: paperSx,
        },
      }}
    >
      <Box className="relative">
        {showCloseButton ? (
          <IconButton
            aria-label="Close modal"
            onClick={onClose}
            className="!absolute right-4 top-4 z-10"
            size="small"
          >
            <X className="h-4 w-4" />
          </IconButton>
        ) : null}

        {title || description ? (
          <DialogTitle className={cn("px-6 pt-6 pb-0", titleClassName)}>
            {title ? (
              <Typography
                component="h2"
                variant="h6"
                sx={{ fontWeight: 600 }}
              >
                {title}
              </Typography>
            ) : null}
            {description ? (
              <Typography
                component="p"
                variant="body2"
                className={cn("mt-2 text-sm text-gray-500", descriptionClassName)}
              >
                {description}
              </Typography>
            ) : null}
          </DialogTitle>
        ) : null}

        <DialogContent className={cn("px-6 pb-6 pt-4", contentClassName)}>{children}</DialogContent>

        {action ? (
          <Box className="flex justify-end border-t border-slate-200 px-6 py-4">
            <Button
              type="button"
              onClick={action.onClick}
              disabled={action.isDisabled || action.isLoading}
              aria-busy={action.isLoading || undefined}
            >
              {action.label}
            </Button>
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
};

export default CustomModal;
